import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Pagination,
  Tooltip,
  Grid,
  Menu
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Security as SecurityIcon,
  AttachMoney as PricingIcon,
  Campaign as MarketingIcon,
  MoreVert as MoreVertIcon,
  Tune as TuneIcon,
  PersonOutline as ImpersonateIcon,
  Block as SuspendIcon,
  CheckCircle as ReactivateIcon,
  Update as ExtendIcon,
  Upgrade as UpgradeIcon
} from '@mui/icons-material';
import { apiClient } from '../../services/api';
import SalonConfiguration from './SalonConfiguration';
import AdvancedFeatureControl from './AdvancedFeatureControl';
import PriceGuardrailsManager from './PriceGuardrailsManager';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setCredentials } from '../../store/slices/authSlice';

interface Salon {
  id: string;
  tenantId: string;
  name: string;
  email: string;
  phone: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  isActive: boolean;
  createdAt: string;
  // Enhanced governance fields
  salonType: string;
  positioningLocked: boolean;
  crossSalonVisibility: boolean;
  brandProtectionLevel: string;
  operationalStatus: string;
  _count: {
    users: number;
    customers: number;
    appointments: number;
  };
}

interface SalonFormData {
  name: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  ownerPassword: string;
  subscriptionTier: string;
  salonType: string;
}

const SalonManagement: React.FC = () => {
  const [salons, setSalons] = useState<Salon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openConfigDialog, setOpenConfigDialog] = useState(false);
  const [openFeatureDialog, setOpenFeatureDialog] = useState(false);
  const [openPricingDialog, setOpenPricingDialog] = useState(false);
  const [editingSalon, setEditingSalon] = useState<Salon | null>(null);
  const [viewingSalon, setViewingSalon] = useState<Salon | null>(null);
  const [configuringSalon, setConfiguringSalon] = useState<Salon | null>(null);
  const [featureSalon, setFeatureSalon] = useState<Salon | null>(null);
  const [pricingSalon, setPricingSalon] = useState<Salon | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSalon, setSelectedSalon] = useState<Salon | null>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<SalonFormData>({
    name: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    ownerFirstName: '',
    ownerLastName: '',
    ownerEmail: '',
    ownerPassword: '',
    subscriptionTier: 'basic',
    salonType: 'MAIN_BRAND'
  });

  useEffect(() => {
    fetchSalons();
  }, [page, search]);

  const fetchSalons = async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: '10',
        ...(search && { search })
      });
      const response = await apiClient.get(`/tenants?${queryParams}`);
      
      if (response.success) {
        setSalons(response.data.salons);
        setTotalPages(response.data.pagination.pages);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to fetch salons');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSalon = async () => {
    try {
      const response = await apiClient.post('/tenants', formData);
      
      if (response.success) {
        setOpenDialog(false);
        resetForm();
        fetchSalons();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to create salon');
    }
  };

  const handleUpdateSalon = async () => {
    if (!editingSalon) return;

    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        subscriptionTier: formData.subscriptionTier
      };

      const response = await apiClient.put(`/tenants/${editingSalon.id}`, updateData);
      
      if (response.success) {
        setOpenDialog(false);
        setEditingSalon(null);
        resetForm();
        fetchSalons();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to update salon');
    }
  };

  const handleDeleteSalon = async (salonId: string) => {
    if (!window.confirm('Are you sure you want to delete this salon?')) return;

    try {
      const response = await apiClient.delete(`/tenants/${salonId}`);
      
      if (response.success) {
        fetchSalons();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to delete salon');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: {
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      ownerFirstName: '',
      ownerLastName: '',
      ownerEmail: '',
      ownerPassword: '',
      subscriptionTier: 'basic',
      salonType: 'MAIN_BRAND'
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingSalon(null);
    setOpenDialog(true);
  };

  const openEditDialog = (salon: Salon) => {
    setFormData({
      name: salon.name,
      email: salon.email || '',
      phone: salon.phone,
      address: {
        street: '',
        city: '',
        state: '',
        pincode: '',
        country: 'India'
      },
      ownerFirstName: '',
      ownerLastName: '',
      ownerEmail: '',
      ownerPassword: '',
      subscriptionTier: salon.subscriptionTier,
      salonType: salon.salonType
    });
    setEditingSalon(salon);
    setOpenDialog(true);
  };

  const handleViewSalon = (salon: Salon) => {
    console.log('Opening view dialog for salon:', salon.name);
    setViewingSalon(salon);
    setOpenViewDialog(true);
  };

  const openConfigurationDialog = (salon: Salon) => {
    setConfiguringSalon(salon);
    setOpenConfigDialog(true);
  };

  const openFeatureControlDialog = (salon: Salon) => {
    setFeatureSalon(salon);
    setOpenFeatureDialog(true);
  };

  const openPricingGovernanceDialog = (salon: Salon) => {
    setPricingSalon(salon);
    setOpenPricingDialog(true);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, salon: Salon) => {
    setAnchorEl(event.currentTarget);
    setSelectedSalon(salon);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSalon(null);
  };

  const handleApplySalonTemplate = async (salonId: string, salonType: string) => {
    try {
      const response = await apiClient.post(`/tenants/${salonId}/apply-template`, {
        salonType
      });
      
      if (response.success) {
        fetchSalons();
        handleMenuClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to apply salon template');
    }
  };

  const handleImpersonate = async (salon: Salon) => {
    try {
      const usersResponse = await apiClient.get(`/tenants/${salon.tenantId}`);
      if (!usersResponse.success || !usersResponse.data?.users) {
        setError('Failed to fetch salon users');
        return;
      }

      const owner = usersResponse.data.users.find((u: any) => u.role === 'SALON_OWNER');
      if (!owner) {
        setError('No owner found for this salon');
        return;
      }

      const response = await apiClient.post(`/tenants/${salon.tenantId}/impersonate/${owner.id}`);
      
      if (response.success) {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
          sessionStorage.setItem('superAdminToken', currentToken);
        }

        localStorage.setItem('token', response.data.token);
        dispatch(
          setCredentials({
            user: response.data.user,
            token: response.data.token,
          })
        );

        handleMenuClose();
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to start impersonation');
    }
  };

  const handleSuspendTenant = async (tenantId: string, reason: string) => {
    try {
      const response = await apiClient.post(`/tenants/${tenantId}/suspend`, { reason });
      
      if (response.success) {
        fetchSalons();
        handleMenuClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to suspend tenant');
    }
  };

  const handleReactivateTenant = async (tenantId: string) => {
    try {
      const response = await apiClient.post(`/tenants/${tenantId}/reactivate`);
      
      if (response.success) {
        fetchSalons();
        handleMenuClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to reactivate tenant');
    }
  };

  const handleExtendTrial = async (tenantId: string, days: number = 7) => {
    try {
      const response = await apiClient.post(`/tenants/${tenantId}/extend-trial`, { days });
      
      if (response.success) {
        fetchSalons();
        handleMenuClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to extend trial');
    }
  };

  const handleUpgradeTier = async (tenantId: string, tier: string) => {
    try {
      const response = await apiClient.post(`/tenants/${tenantId}/upgrade`, { tier });
      
      if (response.success) {
        fetchSalons();
        handleMenuClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to upgrade tenant');
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'basic': return 'default';
      case 'professional': return 'primary';
      case 'enterprise': return 'secondary';
      default: return 'default';
    }
  };

  const getSalonTypeColor = (salonType: string) => {
    switch (salonType) {
      case 'MAIN_BRAND': return 'primary';
      case 'SILENT_UTILITY': return 'warning';
      case 'EXPERIMENTAL': return 'info';
      case 'PARTNER': return 'secondary';
      default: return 'default';
    }
  };

  const getOperationalStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'SUSPENDED': return 'error';
      case 'MAINTENANCE': return 'warning';
      case 'FROZEN': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Salon Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search salons..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreateDialog}
              >
                Create New Salon
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Salon Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Subscription</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Protection</TableCell>
              <TableCell>Users</TableCell>
              <TableCell>Customers</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : salons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  No salons found
                </TableCell>
              </TableRow>
            ) : (
              salons.map((salon) => (
                <TableRow key={salon.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{salon.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {salon.tenantId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={salon.salonType?.replace('_', ' ') || 'MAIN BRAND'}
                        color={getSalonTypeColor(salon.salonType || 'MAIN_BRAND') as any}
                        size="small"
                      />
                      {salon.positioningLocked && (
                        <Chip
                          label="Locked"
                          color="error"
                          size="small"
                          icon={<SecurityIcon />}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{salon.email}</TableCell>
                  <TableCell>{salon.phone}</TableCell>
                  <TableCell>
                    <Chip
                      label={salon.subscriptionTier.toUpperCase()}
                      color={getTierColor(salon.subscriptionTier) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        label={salon.isActive ? 'Active' : 'Inactive'}
                        color={salon.isActive ? 'success' : 'default'}
                        size="small"
                      />
                      <Chip
                        label={salon.operationalStatus || 'ACTIVE'}
                        color={getOperationalStatusColor(salon.operationalStatus || 'ACTIVE') as any}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={salon.brandProtectionLevel || 'STANDARD'}
                      color={salon.brandProtectionLevel === 'MAXIMUM' ? 'error' : 
                             salon.brandProtectionLevel === 'HIGH' ? 'warning' : 'success'}
                      size="small"
                      icon={<SecurityIcon />}
                    />
                  </TableCell>
                  <TableCell>{salon._count.users}</TableCell>
                  <TableCell>{salon._count.customers}</TableCell>
                  <TableCell>
                    {new Date(salon.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton size="small" onClick={() => handleViewSalon(salon)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Salon">
                      <IconButton size="small" onClick={() => openEditDialog(salon)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="More Actions">
                      <IconButton size="small" onClick={(e) => handleMenuClick(e, salon)}>
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingSalon ? 'Edit Salon' : 'Create New Salon'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Salon Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Subscription Tier</InputLabel>
                <Select
                  value={formData.subscriptionTier}
                  onChange={(e) => setFormData({ ...formData, subscriptionTier: e.target.value })}
                >
                  <MenuItem value="basic">Basic</MenuItem>
                  <MenuItem value="professional">Professional</MenuItem>
                  <MenuItem value="enterprise">Enterprise</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Salon Type</InputLabel>
                <Select
                  value={formData.salonType}
                  onChange={(e) => setFormData({ ...formData, salonType: e.target.value })}
                >
                  <MenuItem value="MAIN_BRAND">Main Brand</MenuItem>
                  <MenuItem value="SILENT_UTILITY">Silent/Utility</MenuItem>
                  <MenuItem value="EXPERIMENTAL">Experimental</MenuItem>
                  <MenuItem value="PARTNER">Partner</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {!editingSalon && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    Owner Information
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Owner First Name"
                    value={formData.ownerFirstName}
                    onChange={(e) => setFormData({ ...formData, ownerFirstName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Owner Last Name"
                    value={formData.ownerLastName}
                    onChange={(e) => setFormData({ ...formData, ownerLastName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Owner Email"
                    type="email"
                    value={formData.ownerEmail}
                    onChange={(e) => setFormData({ ...formData, ownerEmail: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Owner Password"
                    type="password"
                    value={formData.ownerPassword}
                    onChange={(e) => setFormData({ ...formData, ownerPassword: e.target.value })}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Address
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={formData.address.street}
                onChange={(e) => setFormData({
                  ...formData,
                  address: { ...formData.address, street: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.address.city}
                onChange={(e) => setFormData({
                  ...formData,
                  address: { ...formData.address, city: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="State"
                value={formData.address.state}
                onChange={(e) => setFormData({
                  ...formData,
                  address: { ...formData.address, state: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Pincode"
                value={formData.address.pincode}
                onChange={(e) => setFormData({
                  ...formData,
                  address: { ...formData.address, pincode: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.address.country}
                onChange={(e) => setFormData({
                  ...formData,
                  address: { ...formData.address, country: e.target.value }
                })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editingSalon ? handleUpdateSalon : handleCreateSalon}
          >
            {editingSalon ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Salon Details
        </DialogTitle>
        <DialogContent>
          {viewingSalon ? (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Basic Information</Typography>
                <Typography><strong>Name:</strong> {viewingSalon.name}</Typography>
                <Typography><strong>Email:</strong> {viewingSalon.email}</Typography>
                <Typography><strong>Phone:</strong> {viewingSalon.phone}</Typography>
                <Typography><strong>Tenant ID:</strong> {viewingSalon.tenantId}</Typography>
                <Typography><strong>Created:</strong> {new Date(viewingSalon.createdAt).toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Subscription & Status</Typography>
                <Typography>
                  <strong>Subscription Tier:</strong> 
                  <Chip 
                    label={viewingSalon.subscriptionTier.toUpperCase()} 
                    color={getTierColor(viewingSalon.subscriptionTier) as any}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography>
                  <strong>Status:</strong> 
                  <Chip
                    label={viewingSalon.isActive ? 'Active' : 'Inactive'}
                    color={viewingSalon.isActive ? 'success' : 'default'}
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
                <Typography><strong>Subscription Status:</strong> {viewingSalon.subscriptionStatus}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Usage Statistics</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4">{viewingSalon._count.users}</Typography>
                      <Typography variant="caption">Users</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4">{viewingSalon._count.customers}</Typography>
                      <Typography variant="caption">Customers</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4">{viewingSalon._count.appointments}</Typography>
                      <Typography variant="caption">Appointments</Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Typography>Loading salon details...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
          {viewingSalon && (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  setOpenViewDialog(false);
                  openEditDialog(viewingSalon);
                }}
              >
                Edit
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setOpenViewDialog(false);
                  openConfigurationDialog(viewingSalon);
                }}
              >
                Configure
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Configuration Dialog */}
      <Dialog open={openConfigDialog} onClose={() => setOpenConfigDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Salon Configuration - {configuringSalon?.name}
        </DialogTitle>
        <DialogContent>
          {configuringSalon && (
            <SalonConfiguration
              salonId={configuringSalon.id}
              onClose={() => setOpenConfigDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Advanced Feature Control Dialog */}
      <Dialog open={openFeatureDialog} onClose={() => setOpenFeatureDialog(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          Advanced Feature Control - {featureSalon?.name}
        </DialogTitle>
        <DialogContent>
          {featureSalon && (
            <AdvancedFeatureControl
              salon={featureSalon}
              onClose={() => setOpenFeatureDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Price Guardrails Dialog */}
      <Dialog open={openPricingDialog} onClose={() => setOpenPricingDialog(false)} maxWidth="xl" fullWidth>
        <DialogTitle>
          Price Guardrails & Service Restrictions - {pricingSalon?.name}
        </DialogTitle>
        <DialogContent>
          {pricingSalon && (
            <PriceGuardrailsManager
              salon={pricingSalon}
              onClose={() => setOpenPricingDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedSalon) handleImpersonate(selectedSalon);
        }}>
          <ImpersonateIcon sx={{ mr: 1 }} />
          Impersonate Owner
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSalon) openConfigurationDialog(selectedSalon);
          handleMenuClose();
        }}>
          <SettingsIcon sx={{ mr: 1 }} />
          Configuration
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSalon) openFeatureControlDialog(selectedSalon);
          handleMenuClose();
        }}>
          <TuneIcon sx={{ mr: 1 }} />
          Feature Control
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSalon) openPricingGovernanceDialog(selectedSalon);
          handleMenuClose();
        }}>
          <PricingIcon sx={{ mr: 1 }} />
          Pricing Governance
        </MenuItem>
        <MenuItem onClick={() => {
          // Marketing permissions would go here
          handleMenuClose();
        }}>
          <MarketingIcon sx={{ mr: 1 }} />
          Marketing Control
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSalon) handleApplySalonTemplate(selectedSalon.id, 'MAIN_BRAND');
        }}>
          <SecurityIcon sx={{ mr: 1 }} />
          Apply Main Brand Template
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSalon) handleApplySalonTemplate(selectedSalon.id, 'SILENT_UTILITY');
        }}>
          <SecurityIcon sx={{ mr: 1 }} />
          Apply Silent Salon Template
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSalon && selectedSalon.subscriptionStatus === 'trial') {
            handleExtendTrial(selectedSalon.tenantId, 7);
          }
        }}
        disabled={selectedSalon?.subscriptionStatus !== 'trial'}
        >
          <ExtendIcon sx={{ mr: 1 }} />
          Extend Trial (7 days)
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSalon && selectedSalon.isActive) {
            const reason = prompt('Enter suspension reason:');
            if (reason) handleSuspendTenant(selectedSalon.tenantId, reason);
          }
          handleMenuClose();
        }}
        disabled={!selectedSalon?.isActive}
        >
          <SuspendIcon sx={{ mr: 1 }} />
          Suspend Tenant
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSalon && !selectedSalon.isActive) {
            handleReactivateTenant(selectedSalon.tenantId);
          }
        }}
        disabled={selectedSalon?.isActive}
        >
          <ReactivateIcon sx={{ mr: 1 }} />
          Reactivate Tenant
        </MenuItem>
        <MenuItem onClick={() => {
          if (selectedSalon) {
            const tier = prompt('Enter new tier (basic, professional, enterprise):');
            if (tier && ['basic', 'professional', 'enterprise'].includes(tier)) {
              handleUpgradeTier(selectedSalon.tenantId, tier);
            }
          }
          handleMenuClose();
        }}>
          <UpgradeIcon sx={{ mr: 1 }} />
          Upgrade Tier
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedSalon) handleDeleteSalon(selectedSalon.id);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Salon
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default SalonManagement;